import { useCallback, useEffect, useRef, useState } from 'react';
import type { AccountSnapshot } from '../api/trading';
import { money, num, priceCents } from '../format';
import { sideName } from '../market';

export type Toast = { id: number; text: string; tone: 'up' | 'down' | 'info' };
const LIFETIME = 7000;

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(0);
  const notify = useCallback((text: string, tone: Toast['tone'] = 'info') => {
    const id = ++next.current;
    setToasts(t => [...t.slice(-3), { id, text, tone }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), LIFETIME);
  }, []);
  return { toasts, notify, dismiss: (id: number) => setToasts(t => t.filter(x => x.id !== id)) };
}

/** Announces fills that arrive after the first account snapshot; earlier fills are history, not news. */
export function useFillAlerts(snapshot: AccountSnapshot | null, notify: (text: string, tone?: Toast['tone']) => void) {
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!snapshot) return;
    if (seen.current == null) { seen.current = new Set(snapshot.fills.map(f => f.id)); return; }
    for (const f of snapshot.fills) {
      if (seen.current.has(f.id)) continue;
      seen.current.add(f.id);
      const realized = f.realized_pnl == null ? '' : ` · realised ${Number(f.realized_pnl) >= 0 ? '+' : '−'}${money(Math.abs(Number(f.realized_pnl)))}`;
      notify(`Filled · ${f.action === 'buy' ? 'Bought' : 'Sold'} ${num(f.quantity)} ${sideName(f.side, f.ticker)} ${f.label} @ ${priceCents(f.price)} · ${f.action === 'buy' ? 'paid' : 'received'} ${money(f.amount)}${realized}`,
        f.realized_pnl != null && Number(f.realized_pnl) < 0 ? 'down' : 'up');
    }
  }, [snapshot, notify]);
}

export function Toasts({ toasts, dismiss }: { toasts: Toast[]; dismiss: (id: number) => void }) {
  return <div className="toasts" role="status" aria-live="polite" data-testid="toasts">
    {toasts.map(t => <div key={t.id} className={`toast ${t.tone}`}><span>{t.text}</span><button aria-label="Dismiss" onClick={() => dismiss(t.id)}>×</button></div>)}
  </div>;
}
